import { type FormEvent, type ReactNode, useState } from 'react';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';

const { t } = i18n;

interface PartnerEditable {
  id: string;
  name: string;
  defaultSlaWorkdays: number;
  allowOperatorOrders: boolean;
  placersSeeOrgOrders: boolean;
  contactPerson: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  billingAddress: string | null;
  vatId: string | null;
  notes: string | null;
  billingMode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY';
  customRateCents: number | null;
}

export function EditPartnerDialog({
  partner,
  onClose,
  onSaved,
}: {
  partner: PartnerEditable;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(partner.name);
  const [defaultSlaWorkdays, setDefaultSlaWorkdays] = useState(partner.defaultSlaWorkdays);
  const [allowOperatorOrders, setAllowOperatorOrders] = useState(partner.allowOperatorOrders);
  const [placersSeeOrgOrders, setPlacersSeeOrgOrders] = useState(partner.placersSeeOrgOrders);
  const [contactPerson, setContactPerson] = useState(partner.contactPerson ?? '');
  const [contactEmail, setContactEmail] = useState(partner.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(partner.contactPhone ?? '');
  const [billingAddress, setBillingAddress] = useState(partner.billingAddress ?? '');
  const [vatId, setVatId] = useState(partner.vatId ?? '');
  const [notes, setNotes] = useState(partner.notes ?? '');
  const [billingMode, setBillingMode] = useState(partner.billingMode);
  const [rate, setRate] = useState(
    partner.customRateCents != null ? (partner.customRateCents / 100).toString() : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api(`/partners/${partner.id}`, {
        method: 'PATCH',
        body: {
          name,
          defaultSlaWorkdays,
          allowOperatorOrders,
          placersSeeOrgOrders,
          contactPerson: contactPerson.trim() || undefined,
          contactEmail: contactEmail.trim() || undefined,
          contactPhone: contactPhone.trim() || undefined,
          billingAddress: billingAddress.trim() || undefined,
          vatId: vatId.trim() || undefined,
          notes: notes.trim() || undefined,
          billingMode,
          customRateCents: rate.trim() === '' ? null : Math.round(parseFloat(rate) * 100),
        },
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_input') {
        setError(t('partner.edit.invalid_input'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('partner.edit.title')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <Field label={t('partner.col.name')}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>
        <Field label={t('partner.col.sla')}>
          <NumberInput
            integer
            min={0}
            max={365}
            value={defaultSlaWorkdays}
            onChange={(v) => setDefaultSlaWorkdays(v ?? 0)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
          />
        </Field>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={allowOperatorOrders}
            onChange={(e) => setAllowOperatorOrders(e.target.checked)}
          />
          {t('partner.create.allow_operator_orders')}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={placersSeeOrgOrders}
            onChange={(e) => setPlacersSeeOrgOrders(e.target.checked)}
          />
          {t('partner.edit.placers_see_org_orders')}
        </label>
        <Field label={t('customer.contact.person')}>
          <input
            value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t('customer.contact.email')}>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </Field>
          <Field label={t('customer.contact.phone')}>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </Field>
        </div>
        <Field label={t('customer.col.billing_address')}>
          <textarea
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
            rows={2}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>
        <Field label={t('customer.col.vat_id')}>
          <input
            value={vatId}
            onChange={(e) => setVatId(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>
        <Field label={t('customer.col.billing_mode')}>
          <select
            value={billingMode}
            onChange={(e) => setBillingMode(e.target.value as 'PER_PALLET_DAY' | 'FIXED_MONTHLY')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="PER_PALLET_DAY">{t('customer.billing.PER_PALLET_DAY')}</option>
            <option value="FIXED_MONTHLY">{t('customer.billing.FIXED_MONTHLY')}</option>
          </select>
        </Field>
        <Field
          label={
            billingMode === 'FIXED_MONTHLY'
              ? t('billing.custom_rate.month')
              : t('billing.custom_rate.day')
          }
        >
          <input
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder={t('billing.custom_rate.placeholder')}
            className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
          />
          <p className="mt-1 text-xs text-muted-foreground">{t('billing.custom_rate.hint')}</p>
        </Field>
        <Field label={t('customer.col.notes')}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </Field>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
